'use client';

import React, { useState, useEffect } from 'react';
import StoryCard from './StoryCard';
import CategoryFilter from './CategoryFilter';
import FeaturedStoryCarousel from './FeaturedStoryCarousel';
import GlobalStoryMap from './GlobalStoryMap';
import StorySubmissionCTA from './StorySubmissionCTA';
import StoryMetrics from './StoryMetrics';
import Icon from '@/components/ui/AppIcon';
import StorySubmissionForm from '@/app/homepage/components/StorySubmissionForm';

interface Story {
  id: number;
  title: string;
  author: string;
  location: string;
  category: string;
  image: string;
  alt: string;
  excerpt: string;
  date: string;
  views: number;
  shares: number;
  votes: number;
}

interface FeaturedStory {
  id: number;
  title: string;
  author: string;
  location: string;
  image: string;
  alt: string;
  quote: string;
  videoUrl?: string;
}

interface MapStory {
  id: number;
  location: string;
  country: string;
  lat: number;
  lng: number;
  count: number;
  category: string;
}

interface Category {
  id: string;
  label: string;
  count: number;
}

interface Metric {
  label: string;
  value: string;
  icon: string;
  trend: string;
}

const CommunityStoriesInteractive = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [isHydrated, setIsHydrated] = useState(false);
  const [showSubmissionForm, setShowSubmissionForm] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories: Category[] = [
  { id: 'all', label: 'All Stories', count: 847 },
  { id: 'personal', label: 'Personal Transformation', count: 312 },
  { id: 'family', label: 'Family Healing', count: 189 },
  { id: 'community', label: 'Community Reconciliation', count: 234 },
  { id: 'institutional', label: 'Institutional Change', count: 112 }];


  const featuredStories: FeaturedStory[] = [
  {
    id: 1,
    title: 'From Hatred to Healing: A Mother\'s Journey',
    author: 'Sarah Mitchell',
    location: 'Belfast, Northern Ireland',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e31eedf6-1765354600236.png",
    alt: 'Middle-aged woman with gray hair in blue sweater sitting peacefully in sunlit room with hands clasped',
    quote: 'I lost my son to violence, but I refused to let hatred consume me. Choosing forgiveness saved my life and inspired my community.',
    videoUrl: 'https://example.com/video1'
  },
  {
    id: 2,
    title: 'Bridging Divides: A Village United',
    author: 'Ahmed Hassan',
    location: 'Kigali, Rwanda',
    image: "https://images.unsplash.com/photo-1630569266941-f8a348786bf0",
    alt: 'Diverse group of African villagers standing together in circle holding hands outdoors with green hills in background',
    quote: 'Our village was torn apart by conflict. Through the No Enemy Festival, we learned to see each other as neighbors again.'
  },
  {
    id: 3,
    title: 'Breaking Generational Cycles',
    author: 'Maria Rodriguez',
    location: 'Medellín, Colombia',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fd052a23-1763294126897.png",
    alt: 'Young Hispanic woman with long dark hair in white shirt smiling warmly at camera in bright indoor setting',
    quote: 'My family carried grudges for three generations. I decided to be the one who breaks the cycle of resentment.',
    videoUrl: 'https://example.com/video3'
  }];


  const mapStories: MapStory[] = [
  { id: 1, location: 'Belfast', country: 'Northern Ireland', lat: 54.5973, lng: -5.9301, count: 47, category: 'Community Reconciliation' },
  { id: 2, location: 'Kigali', country: 'Rwanda', lat: -1.9536, lng: 30.0606, count: 89, category: 'Community Reconciliation' },
  { id: 3, location: 'Medellín', country: 'Colombia', lat: 6.2442, lng: -75.5812, count: 62, category: 'Family Healing' },
  { id: 4, location: 'Jerusalem', country: 'Israel', lat: 31.7683, lng: 35.2137, count: 53, category: 'Personal Transformation' },
  { id: 5, location: 'Cape Town', country: 'South Africa', lat: -33.9249, lng: 18.4241, count: 71, category: 'Institutional Change' },
  { id: 6, location: 'Mumbai', country: 'India', lat: 19.0760, lng: 72.8777, count: 94, category: 'Community Reconciliation' }];


  const stories: Story[] = [
  {
    id: 1,
    title: 'Forgiving My Father After 20 Years',
    author: 'Jennifer Chen',
    location: 'San Francisco, USA',
    category: 'Personal Transformation',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_119cb8b32-1763300639939.png",
    alt: 'Asian woman in her 40s with short black hair in gray blazer sitting thoughtfully by window with soft natural lighting',
    excerpt: 'For two decades, I carried the weight of abandonment. The 30-day preparation journey helped me understand that forgiveness was not about excusing his actions, but about freeing myself from the prison of resentment.',
    date: '2026-01-15',
    views: 12847,
    shares: 342,
    votes: 1289
  },
  {
    id: 2,
    title: 'Neighbors Again: Healing After Ethnic Conflict',
    author: 'David Okonkwo',
    location: 'Lagos, Nigeria',
    category: 'Community Reconciliation',
    image: "https://images.unsplash.com/photo-1643379729592-dd0b5b5b3c72",
    alt: 'African man in traditional colorful patterned shirt smiling warmly with arms crossed standing outdoors',
    excerpt: 'Our neighborhood was divided by ethnic tensions for years. Through SIG Day activities, we organized joint meals and storytelling sessions. Today, our children play together without knowing the hatred their parents once held.',
    date: '2026-01-12',
    views: 9234,
    shares: 287,
    votes: 956
  },
  {
    id: 3,
    title: 'From Divorce to Co-Parenting Peace',
    author: 'Michael Thompson',
    location: 'Toronto, Canada',
    category: 'Family Healing',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_131af0112-1763293927904.png",
    alt: 'Caucasian man in his 30s with beard wearing casual blue shirt smiling confidently in modern office setting',
    excerpt: 'My divorce was bitter and our children suffered. Learning to see my ex-wife without the lens of anger transformed our family dynamic. We now celebrate holidays together and our kids are thriving.',
    date: '2026-01-10',
    views: 15632,
    shares: 421,
    votes: 1547
  },
  {
    id: 4,
    title: 'School Bullying: A Journey to Reconciliation',
    author: 'Emma Wilson',
    location: 'Sydney, Australia',
    category: 'Personal Transformation',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e94971be-1765231180054.png",
    alt: 'Young woman with blonde hair in yellow sweater sitting at desk with laptop smiling at camera in bright room',
    excerpt: 'I was bullied throughout high school and carried that trauma into adulthood. Through the No Enemy philosophy, I found the courage to reach out to my former bullies. The healing conversations that followed changed my life.',
    date: '2026-01-08',
    views: 18945,
    shares: 534,
    votes: 2103
  },
  {
    id: 5,
    title: 'Corporate Culture Transformation',
    author: 'Robert Kim',
    location: 'Seoul, South Korea',
    category: 'Institutional Change',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_158ce42f5-1769366107832.png",
    alt: 'Asian businessman in navy suit and tie standing confidently with arms crossed in modern glass office building',
    excerpt: 'Our company was plagued by internal conflicts and toxic competition. Implementing SIG Day principles company-wide transformed our workplace culture. Productivity increased 40% and employee satisfaction doubled.',
    date: '2026-01-05',
    views: 11234,
    shares: 298,
    votes: 876
  },
  {
    id: 6,
    title: 'Healing Generational Trauma',
    author: 'Fatima Al-Rashid',
    location: 'Dubai, UAE',
    category: 'Family Healing',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1400f5ad7-1763296600804.png",
    alt: 'Middle Eastern woman with dark hair in elegant white hijab smiling warmly at camera with soft lighting',
    excerpt: 'My grandmother never spoke to her sister for 50 years over a family dispute. I facilitated their reconciliation before my grandmother passed. Witnessing their tearful reunion taught me the priceless value of forgiveness.',
    date: '2026-01-03',
    views: 14567,
    shares: 389,
    votes: 1432
  }];


  const metrics: Metric[] = [
  { label: 'Total Stories Shared', value: '147', icon: 'DocumentTextIcon', trend: '+23%' },
  { label: 'Countries Represented', value: '34', icon: 'GlobeAltIcon', trend: '+12%' },
  { label: 'Lives Transformed', value: '3.2K', icon: 'HeartIcon', trend: '+34%' },
  { label: 'Community Chapters', value: '42', icon: 'UserGroupIcon', trend: '+18%' }];


  const handleReadMore = (id: number) => {
    const story = stories.find((s) => s.id === id);
    if (story) {
      // Scroll to top and show full story details
      window.scrollTo({ top: 0, behavior: 'smooth' });
      alert(`Opening full story: "${story.title}" by ${story.author}\n\nIn a full implementation, this would navigate to a dedicated story detail page.`);
    }
  };

  const handleShare = (id: number) => {
    const story = stories.find((s) => s.id === id);
    if (story && navigator.share) {
      navigator.share({
        title: story.title,
        text: `Check out this inspiring story: ${story.title} by ${story.author}`,
        url: `${window.location.origin}/community-stories?story=${id}`
      }).catch(() => {
        // Fallback to clipboard
        navigator.clipboard.writeText(`${window.location.origin}/community-stories?story=${id}`);
        alert('Story link copied to clipboard!');
      });
    } else if (story) {
      // Fallback for browsers without share API
      navigator.clipboard.writeText(`${window.location.origin}/community-stories?story=${id}`);
      alert('Story link copied to clipboard!');
    }
  };

  const handleVote = (id: number) => {
    const story = stories.find((s) => s.id === id);
    if (story) {
      alert(`Thank you for supporting "${story.title}"! Your vote helps amplify transformative stories in our community.`);
      // In production, this would update the vote count via API
    }
  };

  const handleSubmitStory = () => {
    setShowSubmissionForm(true);
  };

  const handleCloseSubmissionForm = () => {
    setShowSubmissionForm(false);
  };

  const filteredStories = stories.filter((story) => {
    const matchesCategory = activeCategory === 'all' || story.category === categories.find((c) => c.id === activeCategory)?.label;
    const matchesSearch = story.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    story.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
    story.author.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const sortedStories = [...filteredStories].sort((a, b) => {
    switch (sortBy) {
      case 'popular':
        return b.votes - a.votes;
      case 'views':
        return b.views - a.views;
      case 'recent':
      default:
        return new Date(b.date).getTime() - new Date(a.date).getTime();
    }
  });

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="h-20" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <div className="h-96 bg-muted rounded-lg animate-pulse" />
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <div className="h-20" />

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12 space-y-16">
        {/* Hero Section */}
        <section className="text-center space-y-6">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 rounded-full">
            <Icon name="SparklesIcon" size={20} className="text-accent" />
            <span className="text-accent font-cta text-sm font-bold uppercase tracking-wider">
              Community Stories
            </span>
          </div>

          <h1 className="text-4xl lg:text-6xl font-headline font-bold text-foreground leading-tight">
            Stories of Transformation & Healing
          </h1>

          <p className="text-lg lg:text-xl text-muted-foreground font-body max-w-3xl mx-auto leading-relaxed">
            Real people, real journeys, real change. Discover how individuals and communities around
            the world are choosing forgiveness and building peace through the No Enemy movement.
          </p>
        </section>

        {/* Metrics */}
        <StoryMetrics metrics={metrics} />

        {/* Featured Stories Carousel */}
        <section>
          <FeaturedStoryCarousel stories={featuredStories} />
        </section>

        {/* Global Story Map */}
        <section>
          <GlobalStoryMap stories={mapStories} />
        </section>

        {/* Filters and Search */}
        <section className="space-y-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <CategoryFilter
              categories={categories}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory} />


            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1 sm:w-80">
                <Icon
                  name="MagnifyingGlassIcon"
                  size={20}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />

                <input
                  type="text"
                  placeholder="Search stories..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary" />

              </div>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 bg-card border border-border rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary">

                <option value="recent">Most Recent</option>
                <option value="popular">Most Popular</option>
                <option value="views">Most Viewed</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between text-sm text-muted-foreground font-body">
            <span>
              Showing {sortedStories.length} of {stories.length} stories
            </span>
            {searchQuery &&
            <button
              onClick={() => setSearchQuery('')}
              className="text-primary hover:underline">

                Clear search
              </button>
            }
          </div>
        </section>

        {/* Story Grid */}
        <section>
          {sortedStories.length > 0 ?
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {sortedStories.map((story) =>
            <StoryCard
              key={story.id}
              story={story}
              onReadMore={handleReadMore}
              onShare={handleShare}
              onVote={handleVote} />

            )}
            </div> :

          <div className="text-center py-16">
              <Icon name="MagnifyingGlassIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                No stories found
              </h3>
              <p className="text-muted-foreground font-body">
                Try adjusting your filters or search query
              </p>
            </div>
          }
        </section>

        {/* Story Submission CTA */}
        <section>
          <StorySubmissionCTA onSubmitClick={handleSubmitStory} />
        </section>

        {/* Impact Statement */}
        <section className="bg-card rounded-lg p-8 lg:p-12 text-center">
          <div className="max-w-3xl mx-auto space-y-6">
            <Icon name="HeartIcon" size={48} className="text-accent mx-auto" />
            <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground">
              Every Story Matters
            </h2>
            <p className="text-lg text-muted-foreground font-body leading-relaxed">
              Each transformation story shared here represents a victory over hatred, a choice for
              peace, and an inspiration for others. Together, we are building a global community
              where forgiveness is strength and compassion is courage.
            </p>
            <div className="flex flex-wrap justify-center gap-4 pt-4">
              <div className="text-center">
                <div className="text-3xl font-headline font-bold text-primary">847</div>
                <div className="text-sm text-muted-foreground font-body">Stories Shared</div>
              </div>
              <div className="w-px bg-border" />
              <div className="text-center">
                <div className="text-3xl font-headline font-bold text-primary">94</div>
                <div className="text-sm text-muted-foreground font-body">Countries</div>
              </div>
              <div className="w-px bg-border" />
              <div className="text-center">
                <div className="text-3xl font-headline font-bold text-primary">12.4K</div>
                <div className="text-sm text-muted-foreground font-body">Lives Changed</div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Story Submission Form Modal */}
      {showSubmissionForm &&
      <StorySubmissionForm onClose={handleCloseSubmissionForm} />
      }
    </div>);


};

export default CommunityStoriesInteractive;